import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Stack, Warning } from "@phosphor-icons/react";

const STATUS = {
  planificat: { label: "Planificat", cls: "bg-slate-500/15 text-slate-300" },
  in_productie: { label: "În producție", cls: "bg-amber-500/15 text-amber-400" },
  finalizat_fabrica: { label: "Finalizat fabrică", cls: "bg-sky-500/15 text-sky-400" },
  livrat: { label: "Livrat", cls: "bg-violet-500/15 text-violet-400" },
  montat: { label: "Montat", cls: "bg-emerald-500/15 text-emerald-400" },
};
const ORDER = ["planificat", "in_productie", "finalizat_fabrica", "livrat", "montat"];
const BAR = { planificat: "bg-slate-500", in_productie: "bg-amber-500", finalizat_fabrica: "bg-sky-500", livrat: "bg-violet-500", montat: "bg-emerald-500" };

function ContractProgress({ positions }) {
  const total = (positions || []).length;
  if (!total) return null;
  const counts = ORDER.reduce((a, k) => ({ ...a, [k]: positions.filter((p) => (p.status || "planificat") === k).length }), {});
  const montate = counts.montat || 0;
  const pct = Math.round((montate / total) * 100);
  return (
    <div className="mb-3" data-testid="contract-progress">
      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>Progres montaj</span>
        <span className="font-medium text-foreground">{montate}/{total} montate · {pct}%</span>
      </div>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/5" title={ORDER.filter((k) => counts[k] > 0).map((k) => `${STATUS[k].label}: ${counts[k]}`).join(" · ")}>
        {ORDER.map((k) => (counts[k] > 0 ? <div key={k} className={BAR[k]} style={{ width: `${(counts[k] / total) * 100}%` }} /> : null))}
      </div>
    </div>
  );
}

export default function ProductionPositions() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManage = hasRole("admin", "pm");
  const [data, setData] = useState(null);
  const [days, setDays] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/reports/positions").then((r) => { setData(r.data); setDays(String(r.data?.alert_days ?? 21)); }).catch(() => setData({ projects: [], summary: {}, total: 0 }));
  useEffect(() => { load(); }, []);

  const genMissing = async () => {
    if (!window.confirm("Generez automat o poziție de rezervor pentru fiecare contract activ fără poziții?")) return;
    setBusy(true);
    try { const { data: r } = await api.post("/reports/positions/generate-missing"); toast.success(`${r.created} poziții generate`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveDays = async () => {
    const d = parseInt(days, 10);
    if (!d || d < 1) { toast.error("Alege un număr de zile valid"); return; }
    try { await api.put("/reports/positions/alert-config", { days: d }); toast.success(`Alertă setată la ${d} zile`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <div className="px-6 py-8 text-sm text-muted-foreground">Se încarcă…</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="production-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2">
          <Stack size={24} weight="bold" className="text-primary" />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Stadiu fizic rezervoare</p>
            <h1 className="font-head text-3xl font-extrabold tracking-tighter">Producție Rezervoare</h1>
            <p className="text-sm text-muted-foreground">Toate rezervoarele (pozițiile) din contractele active, pe stări.</p>
          </div>
        </div>
        {canManage && (
          <div className="flex items-end gap-2">
            <div className="grid gap-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Alertă „blocat în stare" (zile)</Label>
              <Input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} className="h-9 w-32 text-sm" data-testid="prod-alert-days" />
            </div>
            <Button size="sm" variant="outline" className="h-9" onClick={saveDays} data-testid="prod-alert-save">Salvează</Button>
            <Button size="sm" className="h-9" onClick={genMissing} disabled={busy} data-testid="prod-generate-missing">{busy ? "…" : "Generează poziții implicite"}</Button>
          </div>
        )}
      </div>

      {data.stale_count > 0 && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/[0.06] px-4 py-3 text-sm text-red-300" data-testid="prod-stale-banner">
          <Warning size={18} weight="fill" /> {data.stale_count} rezervor(e) stau de peste {data.alert_days} zile în aceeași stare — verifică blocajele de producție.
        </div>
      )}

      <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="production-summary">
        {ORDER.map((k) => (
          <div key={k} className="rounded-xl border border-border bg-card p-4">
            <div className={`mb-2 inline-block rounded px-2 py-0.5 text-[11px] font-bold ${STATUS[k].cls}`}>{STATUS[k].label}</div>
            <div className="font-head text-3xl font-extrabold" data-testid={`prod-count-${k}`}>{data.summary?.[k] || 0}</div>
          </div>
        ))}
      </div>

      {data.projects.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground" data-testid="production-empty">
          Niciun contract cu rezervoare (poziții) definite. Adaugă poziții din fișa unui proiect, tab „Rezervoare".
        </div>
      ) : (
        <div className="space-y-5">
          {data.projects.map((p) => (
            <div key={p.id} className="rounded-xl border border-border bg-card p-5" data-testid={`prod-project-${p.id}`}>
              <div className="mb-3 flex cursor-pointer items-center justify-between" onClick={() => navigate(`/projects/${p.id}`)}>
                <div>
                  <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
                  <h3 className="font-head text-base font-bold">{p.name}</h3>
                  <p className="text-xs text-muted-foreground">{p.client || "—"}</p>
                </div>
                <span className="text-sm text-muted-foreground">{(p.positions || []).length} rezervoare</span>
              </div>
              <ContractProgress positions={p.positions || []} />
              <div className="grid gap-2 md:grid-cols-2">
                {(p.positions || []).map((pos) => (
                  <div key={pos.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${pos.stale ? "border-red-500/40 bg-red-500/[0.05]" : "border-border"}`}>
                    <div>
                      <span className="font-mono font-bold text-primary">{p.code}{pos.suffix}</span>
                      <span className="ml-2 text-muted-foreground">{pos.name || "—"}</span>
                      {pos.volum_mc && <span className="ml-2 text-xs text-muted-foreground/70">{pos.volum_mc} mc</span>}
                      {pos.stale && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-red-300" title={`Blocat în această stare de peste ${data.alert_days} zile`}><Warning size={11} weight="fill" /> blocat</span>}
                    </div>
                    <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${(STATUS[pos.status] || STATUS.planificat).cls}`}>{(STATUS[pos.status] || STATUS.planificat).label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
